export const BAR_CATEGORY_PALETTE = ['#24d6ff', '#3eebbd', '#ffc879', '#a5baff', '#5b9cff', '#ff8599']
const object = value => value !== null && typeof value === 'object' && !Array.isArray(value)
const axes = value => Array.isArray(value) ? value : value ? [value] : []
const categoryName = value => {
  const name = object(value) ? value.value === undefined ? value.name : value.value : value
  return typeof name === 'string' || typeof name === 'number' ? String(name).trim() : ''
}
export function barCategoryColor (value) {
  const color = typeof value === 'string' ? value.trim() : ''
  return /^(?:#(?:[a-f\d]{3}|[a-f\d]{4}|[a-f\d]{6}|[a-f\d]{8})|(?:rgb|hsl)a?\([\d\s.,%+/-]+\)|transparent|white|black)$/i.test(color) ? color : ''
}
export function barCategoryNames (option = {}) {
  return [...new Set([...axes(option.xAxis), ...axes(option.yAxis)].filter(axis => axis.type === 'category').flatMap(axis => (axis.data || []).map(categoryName)).filter(Boolean))]
}

// 只给本轮渲染副本加样式；分类名称匹配不依赖顺序，也不改变原始数值或缺失点。
function composeBarCategoryColors (generated, option = {}) {
  if (option.barColorMode !== 'category' || !Array.isArray(option.barCategoryColors)) return generated
  const colors = new Map()
  option.barCategoryColors.forEach(row => {
    if (!object(row)) return
    const name = categoryName(row.name), color = barCategoryColor(row.color)
    if (name && color) colors.set(name, color)
  })
  if (!colors.size) return generated
  return { ...generated, series: (generated.series || []).map(series => {
    if (series.type !== 'bar' || !Array.isArray(series.data)) return series
    const x = axes(generated.xAxis)[series.xAxisIndex || 0], y = axes(generated.yAxis)[series.yAxisIndex || 0]
    const category = x && x.type === 'category' ? x : y && y.type === 'category' ? y : null
    if (!category || !Array.isArray(category.data)) return series
    return { ...series, data: series.data.map((datum, index) => {
      const color = colors.get(categoryName(category.data[index]))
      if (!color || datum === null || datum === undefined || datum === '' || datum === '-') return datum
      const item = object(datum) ? datum : { value: Array.isArray(datum) ? datum.slice() : datum }
      const style = object(item.itemStyle) ? item.itemStyle : {}
      return { ...item, itemStyle: { ...style, color, ...(object(style.normal) ? { normal: { ...style.normal, color } } : {}) } }
    }) }
  }) }
}

export const BAR_MAX_HIGHLIGHT_COLOR = '#34deb8'

// 最大值只作用于本轮柱体样式，并列最大值一起高亮，不改写原始数据与分类配色。
export function composeBarPresentation (generated, option = {}) {
  const styled = composeBarCategoryColors(generated, option)
  if (option.barMaxHighlight !== true) return styled
  const color = barCategoryColor(option.barMaxColor) || BAR_MAX_HIGHLIGHT_COLOR
  const valueOf = (datum, series) => {
    const raw = object(datum) ? datum.value : datum
    const y = axes(styled.yAxis)[series.yAxisIndex || 0]
    return finite(Array.isArray(raw) ? raw[y && y.type === 'category' ? 0 : 1] : raw, NaN)
  }
  let maximum = -Infinity
  ;(styled.series || []).forEach(series => {
    if (series.type === 'bar' && Array.isArray(series.data)) series.data.forEach(datum => {
      const value = valueOf(datum, series)
      if (Number.isFinite(value)) maximum = Math.max(maximum, value)
    })
  })
  if (!Number.isFinite(maximum)) return styled
  return { ...styled, series: (styled.series || []).map(series => {
    if (series.type !== 'bar' || !Array.isArray(series.data)) return series
    let changed = false
    const data = series.data.map(datum => {
      if (valueOf(datum, series) !== maximum) return datum
      changed = true
      const item = object(datum) ? datum : { value: Array.isArray(datum) ? datum.slice() : datum }
      const style = object(item.itemStyle) ? item.itemStyle : {}
      return { ...item, itemStyle: { ...style, color, ...(object(style.normal) ? { normal: { ...style.normal, color } } : {}) } }
    })
    return changed ? { ...series, data } : series
  }) }
}

// 使用当前 ECharts 版本支持的明确位置；保留高级配置中的显式位置和数据项标签。
export function composeBarValueLabels (generated, custom = {}) {
  return { ...generated, series: (generated.series || []).map((series, index) => {
    if (series.type !== 'bar') return series
    const explicit = custom.series && custom.series[index] && custom.series[index].label
    if (explicit && (explicit.position !== undefined || explicit.normal && explicit.normal.position !== undefined)) return series
    const x = axes(generated.xAxis)[series.xAxisIndex || 0], y = axes(generated.yAxis)[series.yAxisIndex || 0]
    const horizontal = y && y.type === 'category', inverse = !!((horizontal ? x : y) || {}).inverse
    const position = horizontal ? inverse ? 'left' : 'right' : inverse ? 'bottom' : 'top'
    const opposite = { left: 'right', right: 'left', top: 'bottom', bottom: 'top' }[position]
    const data = Array.isArray(series.data) ? series.data.map(datum => {
      const item = object(datum) ? datum : {}, value = object(datum) ? datum.value : datum
      const number = finite(Array.isArray(value) ? value[horizontal ? 0 : 1] : value, NaN)
      if (!(number < 0) || item.label && (item.label.position !== undefined || item.label.normal && item.label.normal.position !== undefined)) return datum
      return { ...(object(datum) ? datum : { value: Array.isArray(datum) ? datum.slice() : datum }), label: { ...item.label, position: opposite } }
    }) : series.data
    return { ...series, data, label: { ...series.label, position } }
  }) }
}

const finite = (value, fallback) => ['number', 'string'].includes(typeof value) && String(value).trim() && Number.isFinite(Number(value)) ? Number(value) : fallback
const pixels = (value, extent) => typeof value === 'string' && value.trim().endsWith('%') ? finite(value.slice(0, -1), 0) * extent / 100 : finite(value, 0)

// 为柱端数值保留组件内空间，较大的原边距不缩小；只调整绘图副本，不改变数据和已存布局。
export function fitBarLabelPadding (option, { width = 0, height = 0 } = {}, engine) {
  const grids = axes(option.grid).map(grid => ({ ...grid })), needed = new Map()
  ;(option.series || []).forEach(series => {
    if (series.type !== 'bar' || !Array.isArray(series.data)) return
    const x = axes(option.xAxis)[series.xAxisIndex || 0], y = axes(option.yAxis)[series.yAxisIndex || 0]
    const horizontal = y && y.type === 'category', category = horizontal ? y : x, valueAxis = horizontal ? x : y
    if (!category || !valueAxis) return
    const gridIndex = category.gridIndex || 0, grid = grids[gridIndex]
    if (!grid) return
    series.data.forEach((datum, index) => {
      if (datum === null || datum === undefined || datum === '' || datum === '-') return
      const item = object(datum) ? datum : {}, value = object(datum) ? datum.value : datum
      const numeric = finite(Array.isArray(value) ? value[horizontal ? 0 : 1] : value, NaN)
      if (!Number.isFinite(numeric)) return
      const label = { ...series.label && series.label.normal, ...series.label, ...item.label && item.label.normal, ...item.label }
      if (label.show !== true) return
      const position = label.position
      const side = position === 'outside' ? horizontal ? (numeric < 0 !== !!valueAxis.inverse ? 'left' : 'right') : (numeric < 0 !== !!valueAxis.inverse ? 'bottom' : 'top')
        : ['left', 'right', 'top', 'bottom'].includes(position) ? position : ''
      if (!side) return
      const style = { ...label.textStyle, ...label }, fontSize = Math.max(1, finite(style.fontSize, 12))
      let text = String(Array.isArray(value) ? numeric : value)
      if (typeof label.formatter === 'string') text = label.formatter.replace(/\{c\}/g, text).replace(/\{b\}/g, categoryName((category.data || [])[index])).replace(/\{a\}/g, series.name || '')
      const font = [style.fontStyle || 'normal', style.fontWeight || 'normal', fontSize + 'px', style.fontFamily || 'sans-serif'].join(' ')
      const measured = engine && engine.format && engine.format.getTextRect ? engine.format.getTextRect(text, font, undefined, undefined, style.padding, style.rich) : { width: text.length * fontSize * .7, height: fontSize * 1.4 }
      const distance = Math.max(0, finite(label.distance, 5)), space = Math.ceil((side === 'left' || side === 'right' ? measured.width : measured.height) + distance + 3)
      if (!needed.has(gridIndex)) needed.set(gridIndex, {})
      const sides = needed.get(gridIndex); sides[side] = Math.max(sides[side] || 0, space)
    })
  })
  let changed = false
  grids.forEach((grid, index) => Object.entries(needed.get(index) || {}).forEach(([side, space]) => {
    if (pixels(grid[side], side === 'left' || side === 'right' ? width : height) < space) { grid[side] = space; changed = true }
  }))
  return changed ? { ...option, grid: Array.isArray(option.grid) ? grids : grids[0] } : option
}
